export type ReportSectionHashTarget =
  | 'formulaire'
  | 'views'
  | 'graphs'
  | 'Dating/Fetal Growth'
  | 'Prenatal Exam'
  | 'Upload';

const REPORT_SECTION_HASH_CONFIG: Record<
  ReportSectionHashTarget,
  { canonicalHash: string; aliases: string[] }
> = {
  formulaire: {
    canonicalHash: '#formulaire',
    aliases: ['formulaire', 'form', 'report'],
  },
  views: {
    canonicalHash: '#vue',
    aliases: ['views', 'view', 'vue', 'vues'],
  },
  graphs: {
    canonicalHash: '#graphs',
    aliases: ['graphs', 'graph'],
  },
  'Dating/Fetal Growth': {
    canonicalHash: '#dating-fetal-growth',
    aliases: ['dating-fetal-growth', 'dating', 'growth', 'fetal-growth'],
  },
  'Prenatal Exam': {
    canonicalHash: '#prenatal',
    aliases: ['prenatal', 'prenal'],
  },
  Upload: {
    canonicalHash: '#upload',
    aliases: ['upload', 'uploads', 'media', 'medias'],
  },
};

export const PRENATAL_REPORT_HASH =
  REPORT_SECTION_HASH_CONFIG['Prenatal Exam'].canonicalHash;

function normalizeHash(hash?: string | null): string {
  return String(hash ?? '')
    .replace(/^#/, '')
    .trim()
    .toLowerCase();
}

export function getReportSectionFromHash(
  hash?: string | null
): ReportSectionHashTarget | null {
  const normalized = normalizeHash(hash);
  if (!normalized) {
    return null;
  }

  for (const [section, config] of Object.entries(REPORT_SECTION_HASH_CONFIG) as [
    ReportSectionHashTarget,
    { canonicalHash: string; aliases: string[] },
  ][]) {
    if (config.aliases.includes(normalized)) {
      return section;
    }
  }

  return null;
}

export function getHashForReportSection(
  section: ReportSectionHashTarget
): string {
  return REPORT_SECTION_HASH_CONFIG[section].canonicalHash;
}

export function isReportSectionHash(hash?: string | null): boolean {
  return getReportSectionFromHash(hash) != null;
}

export function isPrenatalReportHash(hash?: string | null): boolean {
  return getReportSectionFromHash(hash) === 'Prenatal Exam';
}

export function buildReportViewerUrl(
  studyInstanceUid: string,
  section: ReportSectionHashTarget = 'Prenatal Exam'
): string {
  const encodedStudyInstanceUid = encodeURIComponent(studyInstanceUid);
  return `/exam/viewer?StudyInstanceUIDs=${encodedStudyInstanceUid}${getHashForReportSection(section)}`;
}

export function buildPrenatalReportViewerUrl(
  studyInstanceUid: string
): string {
  return buildReportViewerUrl(studyInstanceUid, 'Prenatal Exam');
}
